import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, NavLink } from 'react-router-dom';
import { useRecorderContext } from '@/hooks/RecorderProvider';
import { useProfile } from '@/hooks/ProfileProvider';
import { useT } from '@/i18n/react';
import { useTrackingEnvironment } from '@/tracking/environment';
import { PrivacyZoneOnboardingSheet } from '@/screens/onboarding/PrivacyZoneOnboardingSheet';
import {
  FINISH_HOLD_MS,
  HoldFinishButton,
  SwipeFinishButton,
} from '@/components/FinishGestureButtons';
import { GAMEPLAY } from '@/config/gameplay';
import {
  pauseSoundPlayback,
  playHoldSound,
  playSound,
  unlockSounds,
} from '@/lib/sound';
import './Dock.css';

/**
 * Alsó dock — a navigáció gerince ÉS a rögzítés vezérlője.
 *
 * A középső gomb nem csak egy menüpont: ez a képernyő domináns akciója, és
 * a rögzítés állapotától függően mást csinál.
 *
 *   nincs rögzítés   → Play. Máshonnan a rögzítés képernyőre visz, ott indít.
 *   rögzítés fut     → Pause, és két gomb nyúlik ki belőle: Kör és Befejezés.
 *   szüneteltetve    → Play (folytatás), ugyanazzal a két gombbal.
 *   befejezve        → „Új rögzítés" felirat, kiszélesített gombban.
 *
 * A kinyúló gombok miatt a menüpontok a szélek felé csúsznak — erről a
 * `dock--wide` osztály gondoskodik.
 *
 * docs/01-kepernyoterkep.md → Navigációs architektúra
 */
export function Dock() {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const trackingEnvironment = useTrackingEnvironment();
  const {
    state,
    upload,
    begin,
    pause,
    resume,
    markLap,
    finish,
    discard,
    pendingType,
    setPendingType,
    pickerOpen,
    setPickerOpen,
    countdown,
    setCountdown,
    statsFullView,
    finishGesture,
  } = useRecorderContext();
  const { profile } = useProfile();
  const [gateOpen, setGateOpen] = useState(false);

  // LAB E2E-ben ugyanaz a Dock vezérli ugyanazt a recordert, csak az oldal
  // admin útvonalon él. Ilyenkor nem navigálunk el `/rogzites`-re a Play előtt.
  const onTrackingScreen = location.pathname === '/rogzites' || trackingEnvironment.mode === 'lab';
  const idle = state.status === 'idle';
  const running = state.status === 'recording';
  const paused = state.status === 'paused';
  const done = state.status === 'finished';
  const active = running || paused;
  /**
   * TÚL RÖVID RÖGZÍTÉS — Geri kérése (2026-08-27): ilyenkor a TrackingScreen
   * SOSEM tölti fel (nincs mit), tehát `upload.status` örökre `'idle'`
   * marad. A régi „Új rögzítés" gomb ezért a `done` ágban a
   * `upload.status !== 'done'` feltétel miatt csak `navigate('/rogzites')`-t
   * hívott — ami a rögzítés képernyőjén NULLA hatású volt, a gomb
   * láthatóan „nem csinált semmit".
   */
  const tooShortDone = done && state.distanceM < GAMEPLAY.MIN_DISTANCE_M;

  /**
   * INDÍTÁS ELŐTT: 3-2-1 VISSZASZÁMLÁLÁS A GOMBON, MAJD „RAJT!" A KÖZÉPEN.
   *
   * Geri kérése (2026-08-27): a Play gomb második megnyomása nem indítja
   * AZONNAL a mérést — előbb egy 3 másodperces visszaszámlálás fut a gombon
   * magán, utána jelenik meg a „RAJT!" felirat, és CSAK EZUTÁN hívódik a
   * valódi `begin()`. Így a rögzítés kezdő időbélyege pontosan a „RAJT!"
   * pillanatára esik, nem a gombnyomásra.
   */
  const [showRajt, setShowRajt] = useState(false);

  /*
    ⚠️ ITT NINCS `primeSounds()` — ÉS EZ SZÁNDÉKOS, KÉT NÉMULÁS ÁRÁN TANULVA.

    2026-09-08-án ide került egy `primeSounds()` hívás, azzal a céllal, hogy a
    hangok hossza már a Play gombnyomás előtt ismert legyen. A következmény két
    egymás utáni néma iOS build lett (49, 50): az előtöltött elemen a
    `volume = 0` már ÉRVÉNYRE JUT, a néma lejátszás pedig nem aktiválja a
    rendszer hangútvonalát. Lásd `lib/sound.ts` → `unlockElement()`.

    Az előkészítés helye a `TrackingScreen` maradt.
  */

  /**
   * ⚠️ A VISSZASZÁMLÁLÁS EFFEKTJE CSAK A SZÁMRA FIGYELHET — MINDEN MÁS REFBŐL.
   *
   * MÉRT HIBA (Geri, iPhone, 2026-09-09): „a 3-2-1 hangok furák, nem időben
   * jönnek, nem követik a képre kiírt számokat".
   *
   * Ez az effekt EGYSZERRE játszik hangot ÉS birtokol egy 1000 ms-os időzítőt.
   * A függőségei közt viszont ott volt a `begin` és a `pendingType` is — a
   * `begin` pedig `useCallback`, ami a `pendingType`, `attach`, `apply`,
   * `positionSource`, `persister` és három további érték bármelyikének
   * változásakor ÚJ azonosságot kap (`useRecorder.ts`). Minden ilyen változás
   * újrafuttatta az effektet: eldobta a még futó időzítőt, indított egy újat,
   * és közben LEJÁTSZOTT MÉG EGY SÍPOT — a hang tehát elcsúszott a kiírt
   * számtól, és többször is megszólalhatott ugyanarra a számra.
   *
   * A refek pontosan azt a kettősséget oldják fel, hogy a hívás mindig a
   * LEGFRISSEBB `begin`-t használja, az ÜTEMEZÉST viszont kizárólag a
   * `countdown` vezérli. Ugyanaz a minta, mint a `useCellStepSound`
   * birtokviszony-refje.
   */
  const beginRef = useRef(begin);
  beginRef.current = begin;
  const pendingTypeRef = useRef(pendingType);
  pendingTypeRef.current = pendingType;

  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) {
      setCountdown(null);
      // A „RAJT!" hangja a felirattal EGYSZERRE — Geri kérése (2026-09-01).
      playSound('count-down-start');
      const type = pendingTypeRef.current;
      if (type) void beginRef.current(type);
      setShowRajt(true);
      return;
    }
    /*
      A síp a SZÁM MEGJELENÉSEKOR szól, nem az időzítő lejártakor: a hatás
      minden új értékre újrafut (3 → 2 → 1), tehát pontosan háromszor. A
      hangzár feloldása a gombnyomásnál megtörtént (`primaryAction`), így az
      első síp sem esik ki.
    */
    playSound('count-down-beep');
    const timer = window.setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown, setCountdown]);

  /**
   * FUTÓ MÉRÉS KÖZBEN A RÖGZÍTÉS KÉPERNYŐJE RAGADÓS.
   *
   * A böngésző „vissza" gombja ZSÁKUTCÁBA vitt (Geri, 2026-08-26): a Home
   * jelent meg, a dokk viszont a rögzítés vezérlőit mutatta — és mivel az
   * `active` állapotban a dokk CSAK a három gombot tartalmazza, menüpontok
   * nélkül, a felhasználó nem tudott sehova továbblépni. A képernyő és a dokk
   * két külön dolgot állított, és egyik sem vezetett ki.
   *
   * A történelmi navigációt nem lehet megbízhatóan letiltani (és nem is
   * volna helyes elvenni a böngésző gombját), ezért a másik irányt
   * választottuk: bármit tölt be a vissza/előre, azonnal visszaváltunk a
   * mérésre. A `replace` szándékos — így a felhasználó nem tud egy hosszú
   * oda-vissza láncot építeni magának a nyomógombbal.
   *
   * ⚠️ A LAB E2E-t KI KELL HAGYNI: ott a rögzítés admin útvonalon fut, és egy
   * `/rogzites`-re dobás kirántaná a mérést a saját környezetéből.
   */
  useEffect(() => {
    if (!active || onTrackingScreen) return;
    navigate('/rogzites', { replace: true });
  }, [active, onTrackingScreen, navigate]);

  /**
   * A VÁLASZTÓ BEZÁRUL, HA ELHAGYJUK A RÖGZÍTÉS KÉPERNYŐJÉT.
   *
   * ⚠️ MÉRT HIBA (2026-09-02, Geri visszajelzése): a Play gomb nyitja a
   * választót és átvisz a `/rogzites`-re (lásd `primaryAction`), de ha innen
   * a Dock egy MÁSIK menüpontjára navigálunk (nem a Play gombbal, hanem pl.
   * a Home ikonnal), semmi nem zárta be a `pickerOpen`-t. A gomb ettől
   * PERMANENSEN elakadt: a `picking` (`idle && pickerOpen && !pendingType`)
   * igaz maradt, tehát a sárga felfelé nyíl ÉS a `disabled` állapot is
   * megmaradt — a középső gomb többé nem volt megnyomható.
   *
   * ⚠️ ÁTMENETRE FIGYELÜNK, NEM ÁLLAPOTRA — és ez nem finomkodás. Az első
   * változat azt nézte, hogy „nyitva a választó ÉS nem a rögzítésen
   * vagyunk". Ez ELBUKOTT (mérve, helyi emulátoron): a `primaryAction`
   * ugyanabban a koppintásban nyitja a választót ÉS indítja a navigációt, a
   * React Router útvonala viszont csak a KÖVETKEZŐ renderben áll át. Abban a
   * köztes renderben a feltétel igaz volt, tehát az effekt bezárta a
   * választót, mielőtt a rögzítés képernyője felépült volna — a Play gomb
   * látszólag „nem csinált semmit".
   *
   * A `wasOnTrackingScreen` ref ezért a KILÉPÉST figyeli: csak akkor
   * zárunk, ha az előző renderben még a rögzítésen voltunk, most pedig már
   * nem. Az odafelé vezető úton (`false → true`) nem szólal meg.
   *
   * A `countdown !== null` kizárása szándékos: a visszaszámlálás alatt a
   * `pendingType` NEM nullázódhat, különben a `begin(pendingType)` hívás
   * (lásd fent) néma marad, amikor a számláló lejár.
   */
  const wasOnTrackingScreen = useRef(onTrackingScreen);
  useEffect(() => {
    const left = wasOnTrackingScreen.current && !onTrackingScreen;
    wasOnTrackingScreen.current = onTrackingScreen;
    if (!left || !idle || !pickerOpen || countdown !== null) return;
    setPickerOpen(false);
    setPendingType(null);
  }, [idle, pickerOpen, onTrackingScreen, countdown, setPickerOpen, setPendingType]);

  function primaryAction() {
    /**
     * ⚠️ A HANGZÁR FELOLDÁSA ITT, ÉS SEHOL MÁSHOL.
     *
     * Minden mai böngésző csak felhasználói gesztusból engedi az első
     * lejátszást — iOS-en elemenként. Ez az a koppintás, ami minden rögzítés
     * előtt biztosan megtörténik, és még a visszaszámlálás ELŐTT van, tehát
     * az első síp sem késik le. A hívás idempotens (lásd `sound.ts`).
     */
    unlockSounds();
    if (running) {
      pause();
      playSound('pause-activity');
      return;
    }
    if (paused) {
      resume();
      playSound('resume-activity');
      return;
    }
    if (done) {
      /**
       * TÚL RÖVID RÖGZÍTÉS: NINCS MIT VÉDENI. Se terület, se GP nem jár érte
       * (lásd a TrackingScreen figyelmeztetését) — az alábbi, mentett-mérést
       * védő szabály itt nem vonatkozik, mert nincs mit menteni. A gomb
       * ilyenkor amúgy is a sima Play ikont mutatja (`showFinishedLabel`
       * lent), tehát ez a koppintás egyenesen egy friss indítást takar.
       */
      if (tooShortDone) return void discard();
      /**
       * ⚠️ MENTETLEN MÉRÉST SOHA NEM DOBUNK EL EGY KOPPINTÁSRA.
       *
       * A `discard()` VÉGLEG törli a megőrzött rögzítést. Korábban a `done`
       * ág feltétel nélkül ezt hívta — és ez éles adatvesztéshez vezetett
       * (2026-08-26): a rögzítés képernyőjéről elnavigálva a gomb nem is
       * „Új rögzítés" feliratot viselt, hanem egy Play ikont (a felirathoz
       * `onTrackingScreen` is kell), tehát a felhasználó a folytatás
       * szándékával nyomta meg — és ezzel törölte a saját futását.
       *
       * Amíg a feltöltés nincs kész, ez a gomb a rögzítés képernyőjére visz,
       * ahol LÁTSZIK, mi történt (küldés, hiba, újrapróbálás). Eldobni csak
       * onnan lehet, tudatosan.
       */
      if (upload.status !== 'done') {
        if (!onTrackingScreen) return navigate('/rogzites');
        /**
         * ⚠️ A RÖGZÍTÉS KÉPERNYŐJÉN EZ A GOMB KORÁBBAN NÉMA VOLT. A fenti
         * navigáció ide mutat, tehát itt semmit nem csinált — hibás mentésnél
         * a felhasználó beragadt (2026-09-24: egy valójában elmentett 120 km-es
         * futás „A mentés nem sikerült" üzenettel, működésképtelen „Új
         * rögzítés" gombbal). Hibánál most tudatos, megerősített eldobás;
         * küldés közben továbbra sem dobunk el semmit.
         */
        if (state.awaitingSave === true && upload.status === 'idle') {
          // Not saved yet (review before Save): same deliberate, confirmed discard.
          if (window.confirm(t.tracking.reviewDiscardConfirm)) void discard();
          return;
        }
        if (upload.status !== 'error') return;
        const confirmed = window.confirm(t.dock.discardUnconfirmedActivity);
        if (confirmed) void discard();
        return;
      }
      return void discard();
    }
    // Visszaszámlálás közben a gomb inert — nincs mit kezdeni egy újabb
    // koppintással, amíg a „RAJT!" felé tartunk. Ha közben elnavigáltunk, a
    // koppintás visszavisz, hogy a felhasználó lássa, mi történik.
    if (countdown !== null) return onTrackingScreen ? undefined : void navigate('/rogzites');
    if (!pickerOpen) {
      /**
       * ELSŐ KOPPINTÁS: NYITJA A VÁLASZTÓT — AKÁRHONNAN.
       *
       * ⚠️ Ez korábban KÉT koppintás volt máshonnan indulva: a dokk Play
       * gombja előbb csak átvitt a rögzítés oldalra, és ott kellett újra
       * megnyomni, hogy a mozgásforma-választó megjelenjen. Geri kérése
       * (2026-09-02): a Play gomb BÁRHOL ugyanoda vigyen — egyenesen arra a
       * lépésre, ahol már választani lehet.
       *
       * A `pickerOpen` a recorder KÖZÖS állapota (`useRecorder`), nem a
       * képernyőé, ezért túléli az útvonalváltást: mire a `TrackingScreen`
       * felépül, a választó már nyitva van (a választót ő rajzolja, nem a
       * dokk). A sorrend így is fontos — előbb az állapot, utána a
       * navigáció, hogy egyetlen újrarajzolásban történjen meg mindkettő.
       *
       * A `pendingType` nullázása marad: a választó MINDIG üresen nyílik,
       * sose emlékezzen az előző mozgásformára (Geri, 2026-08-27).
       */
      setPendingType(null);
      setPickerOpen(true);
      if (!onTrackingScreen) navigate('/rogzites');
      return;
    }
    // A választó nyitva van, de máshova navigáltunk közben — vissza a
    // rögzítéshez, hogy a következő koppintás a választón történjen.
    if (!onTrackingScreen) return navigate('/rogzites');
    if (!pendingType) return; // még nincs választva — a gomb ilyenkor `disabled`.
    /**
     * A PRIVÁT ZÓNA ONBOARDING-KAPUJA (GRUNDO #65, `docs/02` → Onboarding 5.).
     *
     * ⚠️ ITT, NEM AZ EGÉSZ APP ELŐTT: élesben már vannak felhasználók, akiknél
     * `privacyZoneSetAt` null, mert ez a lépés még sosem futott le. Az első
     * rögzítés-indítás elé állítva pontosan a spec szövegét követi („mielőtt
     * az első aktivitás elindulna"), és a meglévő, aktív felhasználókat nem
     * lepi meg egy hirtelen teljes appzárolással.
     */
    if (profile?.privacy.privacyZoneSetAt == null) {
      setPickerOpen(false);
      setGateOpen(true);
      return;
    }
    setPickerOpen(false);
    setCountdown(3);
  }

  /** A választóban NINCS kiválasztva semmi — a gomb sárga, ↑. */
  const picking = idle && pickerOpen && !pendingType;

  /**
   * ⚠️ A GOMB CSAK A RÖGZÍTÉS KÉPERNYŐJÉN LEHET INAKTÍV — KÜLÖNBEN CSAPDA.
   *
   * MÉRT HIBA (Geri, iPhone): a Home-on a Play gombra a kép NEM váltott át, a
   * gomb viszont sárga ↑-re állt, mozgásforma-választó sehol, és a dokk
   * használhatatlanná vált — csak app-újraindítás segített.
   *
   * AZ OK NEM A LASSÚSÁG, HANEM A KIÚT HIÁNYA. A `primaryAction` egyetlen
   * koppintásban állítja a `pickerOpen`-t ÉS indítja a navigációt, a
   * `TrackingScreen` viszont `lazy()` (App.tsx), a react-router v7 pedig
   * minden navigációt `startTransition`-be csomagol — amíg a chunk töltődik,
   * React SZÁNDÉKOSAN a régi képernyőt (a Home-ot) hagyja kint. Az első
   * navigációnál ez a chunk hidegen töltődik, tehát a köztes állapot NORMÁLIS.
   *
   * A baj az, hogy ebből az állapotból nem lehetett kilépni: a `picking`
   * azonnal igazzá vált, a gomb `disabled` lett, a `wasOnTrackingScreen`
   * mentőeffekt pedig csak a KILÉPÉST figyeli (lásd fent) — ide sosem
   * érkeztünk meg, tehát nem oldott ki semmit.
   *
   * A mentőág már meg van írva: nyitott választónál a rögzítésen kívülről a
   * `primaryAction` újra navigál (lásd lent). Eddig csak elérhetetlen volt.
   * Ezért a tiltás a rögzítés képernyőjére szűkül — ott a választó LÁTSZIK,
   * tehát az inaktív gomb értelmes visszajelzés („válassz felül"); máshol a
   * gomb maradjon nyomható, hogy a második koppintás kivezessen.
   */
  const primaryDisabled = picking && onTrackingScreen;
  /** Van választott mozgásforma, VAGY fut a visszaszámlálás — piros, kész. */
  const armed = (idle && pickerOpen && !!pendingType) || countdown !== null;

  // Túl rövid rögzítésnél NEM a kiszélesített „Új rögzítés" felirat jön —
  // a sima Play ikon jelzi, hogy ez gyakorlatilag egy friss indítás lesz.
  const showFinishedLabel = done && onTrackingScreen && !tooShortDone;

  const primaryLabel = running
    ? t.dock.pause
    : paused
      ? t.dock.resume
      : showFinishedLabel
        ? t.dock.newRecording
        : countdown !== null
          ? t.dock.startsIn(countdown)
          : picking
            ? t.dock.chooseActivityType
            : t.dock.startActivity;

  function startNewLap() {
    markLap();
    playSound('new-lap');
  }

  function finishActivity() {
    pauseSoundPlayback('pressing-finish-activity', true);
    playSound('finish-activity');
    void finish();
  }

  const controls = (
    <div className="dock__center">
      {active ? (
        /*
          SZÜNETBEN NEM LEHET KÖRT NYITNI (Geri, 2026-08-26). Álló mérésnél a
          kör kezdete értelmezhetetlen: nincs mozgás, amit elválasztana, és a
          folytatás pillanatában amúgy is szakadás van a nyomvonalban.
        */
        <button className="dock__side dock__side--left" onClick={startNewLap} disabled={paused}>
          {t.dock.newLap}
        </button>
      ) : null}

      <button
        className={`dock__play${showFinishedLabel ? ' dock__play--wide' : ''}${
          idle && !pickerOpen && countdown === null ? ' dock__play--idle' : ''
        }${active ? ' dock__play--large' : ''}${picking ? ' dock__play--picking' : ''}${
          armed ? ' dock__play--armed' : ''
        }${paused ? ' dock__play--paused' : ''}`}
        onClick={primaryAction}
        disabled={primaryDisabled}
        aria-label={primaryLabel}
        data-tour="start"
      >
        {countdown !== null ? (
          <span className="dock__play-countdown">{countdown}</span>
        ) : showFinishedLabel ? (
          <span className="dock__play-text">{t.dock.newRecording}</span>
        ) : running ? (
          <PauseIcon />
        ) : picking ? (
          <UpArrowIcon />
        ) : (
          <PlayIcon />
        )}
      </button>

      {active ? (
        finishGesture === 'swipe' ? (
          <SwipeFinishButton onFinish={finishActivity} />
        ) : (
          <HoldFinishButton
            onFinish={finishActivity}
            /* A hang a sáv AKTUÁLIS állásáról indul, nem onnan, ahol legutóbb
               abbahagytuk — a sáv ugyanis közben visszafolyik. */
            onHoldStart={(progress) =>
              playHoldSound('pressing-finish-activity', progress, FINISH_HOLD_MS)
            }
            onHoldPause={() => pauseSoundPlayback('pressing-finish-activity')}
            onHoldReset={() => pauseSoundPlayback('pressing-finish-activity', true)}
          />
        )
      ) : null}

      {/*
        „RAJT!" — a visszaszámlálás VÉGÉN jelenik meg, ugyanazzal a portál-
        technikával, mint a befejezés-visszajelzés: a `body`-ba megy, hogy a
        `dock`/`dock__center` `overflow`-ja ne vágja le. Az `onAnimationEnd`
        szedi le magát — nincs külön timer, ami elszaladhatna az animációtól.
      */}
      {showRajt
        ? createPortal(
            <div
              className="rajt-overlay"
              aria-hidden="true"
              onAnimationEnd={() => setShowRajt(false)}
            >
              {t.dock.go}
            </div>,
            document.body,
          )
        : null}
    </div>
  );

  /*
    A statisztika-panel teljes nézetében a dokk „beleolvad" a panelbe —
    Geri kérése (2026-08-27): azonos háttér, se keret, se árnyék, se blur,
    hogy a kettő egy felületnek hasson. Lásd `Dock.css` → `.dock--blend`.

    Szünetben a teljes nézet MAGA VÁLT SÁRGÁRA (`.track__panel--paused`,
    tracking.css) — a dokknak KÖVETNIE kell, különben a beolvadás pont
    akkor törne meg, amikor a felhasználó megáll.
  */
  const dockClassName = `dock${statsFullView ? ' dock--blend' : ''}${
    statsFullView && paused ? ' dock--paused' : ''
  }`;

  const gate = gateOpen ? (
    <PrivacyZoneOnboardingSheet
      onComplete={() => {
        setGateOpen(false);
        setCountdown(3);
      }}
    />
  ) : null;

  if (active) {
    return (
      <>
        <nav className={`${dockClassName} dock--controls`} aria-label={t.dock.recordingControls}>
          {controls}
        </nav>
        {gate}
      </>
    );
  }

  return (
    <>
      <nav className={dockClassName} aria-label={t.dock.mainNavigationAndRecording}>
        <NavLink to="/" className="dock__item" aria-label={t.dock.home}>
          <HomeIcon />
        </NavLink>
        <NavLink to="/grund" className="dock__item" aria-label={t.dock.grund} data-tour="grund">
          <HexIcon />
        </NavLink>
        {controls}
        <NavLink to="/kozosseg" className="dock__item" aria-label={t.dock.community} data-tour="community">
          <PeopleIcon />
        </NavLink>
        <NavLink to="/profil" className="dock__item" aria-label={t.dock.profile} data-tour="profile">
          <PersonIcon />
        </NavLink>
      </nav>
      {gate}
    </>
  );
}


function PauseIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <rect x="6.5" y="5" width="4" height="14" rx="1.4" />
      <rect x="13.5" y="5" width="4" height="14" rx="1.4" />
    </svg>
  );
}

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const HomeIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" {...stroke}>
    <path d="M3 10.5 12 3l9 7.5" />
    <path d="M5 9.5V20h14V9.5" />
  </svg>
);

const HexIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" {...stroke}>
    <path d="M12 2.5 20 7v10l-8 4.5L4 17V7z" />
  </svg>
);

const PeopleIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" {...stroke}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M2.8 20c0-3.4 2.8-5.6 6.2-5.6s6.2 2.2 6.2 5.6" />
    <path d="M16.5 5.4a3.2 3.2 0 0 1 0 6.1M17.5 14.8c2.3.5 3.9 2.4 3.9 5.2" />
  </svg>
);

const PersonIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" {...stroke}>
    <circle cx="12" cy="8" r="3.4" />
    <path d="M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor">
    <path d="M8 5.5v13l11-6.5z" />
  </svg>
);

/** A mozgásforma-választó nyitott állapotát jelzi — „válassz felül". */
const UpArrowIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 19V5M6 11l6-6 6 6" />
  </svg>
);
